import axios from 'axios';
import { API_BASE_URL } from './config';

// Interfaces
export interface AnalyticsFilters {
  project?: string;
  dateRange?: string;
  startDate?: string;
  endDate?: string;
  includeAI?: boolean;
}

export interface ProjectStats {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  onHoldProjects: number;
  budgetStats: {
    totalBudget: number;
    actualCost: number;
    budgetUtilization: number;
    averageCompletion: number;
  };
}

export interface SafetyStats {
  totalReports: number;
  incidentsByType: Array<{
    type: string;
    count: number;
  }>;
  severityDistribution: Array<{
    severity: string;
    count: number;
  }>;
  monthlyTrends: Array<{
    month: string;
    incidents: number;
    nearMisses: number;
  }>;
  riskScoreAverage: number;
  correctiveActionsOverdue: number;
}

export interface DailyReportStats {
  totalReports: number;
  averageSafetyScore: number;
  totalIssues: number;
  reportsByStatus: Array<{
    status: string;
    count: number;
  }>;
  weeklyTrends: Array<{
    week: string;
    reports: number;
    issues: number;
  }>;
}

export interface ToolboxTalkStats {
  totalTalks: number;
  averageAttendance: number;
  topicDistribution: Array<{
    topic: string;
    count: number;
  }>;
  monthlyTrends: Array<{
    month: string;
    talks: number;
    attendance: number;
  }>;
  completionRate: number;
}

export interface PerformanceMetrics {
  projectCompletion: {
    onTime: number;
    delayed: number;
    ahead: number;
  };
  budgetPerformance: {
    underBudget: number;
    onBudget: number;
    overBudget: number;
  };
  qualityMetrics: {
    averageQualityScore: number;
    defectRate: number;
    reworkRate: number;
  };
  productivityMetrics: {
    averageTaskCompletion: number;
    resourceUtilization: number;
    equipmentEfficiency: number;
  };
}

export interface AnalyticsDashboardData {
  projectStats: ProjectStats;
  safetyStats: SafetyStats;
  dailyReportStats: DailyReportStats;
  toolboxTalkStats: ToolboxTalkStats;
  performanceMetrics: PerformanceMetrics;
  aiInsights?: {
    recommendations: string[];
    riskAlerts: string[];
    trends: string[];
    predictions: string[];
  };
}

export interface ChartData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string;
    borderWidth?: number;
  }>;
}

export interface ReportSummary {
  period: {
    startDate: string;
    endDate: string;
  };
  summary: {
    totalReports: number;
    averageScore: number;
    totalIssues: number;
    completionRate: number;
  };
  reports: Array<{
    id: string;
    date: string;
    type: string;
    project: string;
    reportedBy: string;
    status: string;
    score?: number;
    issuesCount: number;
  }>;
}

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

class AnalyticsService {
  // Get dashboard analytics data
  async getDashboardData(filters: AnalyticsFilters = {}): Promise<AnalyticsDashboardData> {
    const params = new URLSearchParams();
    if (filters.project) params.append('project', filters.project);
    if (filters.dateRange) params.append('dateRange', filters.dateRange);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.includeAI !== undefined) params.append('includeAI', filters.includeAI.toString());
    
    try {
      const response = await api.get(`/analytics/dashboard?${params}`);
      return response.data;
    } catch (error: any) {
      const is404 = error?.response?.status === 404;
      const isNetworkRefused = error?.code === 'ERR_NETWORK' || error?.message?.includes('Network Error');
      const usingDefault5001 = (API_BASE_URL || '').includes('5001');

      if ((is404 || isNetworkRefused) && usingDefault5001) {
        const fallbackBase = 'http://localhost:5002/api';
        const fallback = axios.create({ baseURL: fallbackBase });
        fallback.interceptors.request.use((config) => {
          const token = localStorage.getItem('token');
          if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
          }
          return config;
        });

        const response2 = await fallback.get(`/analytics/dashboard?${params}`);
        console.warn('AnalyticsService: fell back to', fallbackBase);
        return response2.data;
      }

      throw error;
    }
  }

  // Get project statistics
  async getProjectStats(filters: AnalyticsFilters = {}): Promise<ProjectStats> {
    const params: any = {};
    if (filters.project) params.project = filters.project;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    
    const response = await api.get('/projects/dashboard/stats', { params });
    return response.data.data;
  }

  // Get safety statistics
  async getSafetyStats(filters: AnalyticsFilters = {}): Promise<SafetyStats> {
    const params: any = {};
    if (filters.project) params.project = filters.project;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    
    const response = await api.get('/safety-reports/statistics/overview', { params });
    return response.data.data;
  }

  // Get daily report statistics
  async getDailyReportStats(filters: AnalyticsFilters = {}): Promise<DailyReportStats> {
    const params: any = {};
    if (filters.project) params.project = filters.project;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    
    const response = await api.get('/daily-reports/safety/statistics', { params });
    return response.data.data;
  }

  // Get toolbox talk statistics
  async getToolboxTalkStats(filters: AnalyticsFilters = {}): Promise<ToolboxTalkStats> {
    const params: any = {};
    if (filters.project) params.project = filters.project;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    
    const response = await api.get('/toolbox-talks/statistics/overview', { params });
    return response.data.data;
  }

  // Get performance metrics
  async getPerformanceMetrics(filters: AnalyticsFilters = {}): Promise<PerformanceMetrics> {
    const params: any = {};
    if (filters.project) params.project = filters.project;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    
    const response = await api.get('/analytics/performance', { params });
    return response.data.data;
  }

  // Get safety report summary
  async getSafetyReportSummary(startDate: string, endDate: string, project?: string): Promise<ReportSummary> {
    const params: any = { startDate, endDate };
    if (project) params.project = project;
    
    const response = await api.get('/safety-reports/reports/summary', { params });
    return response.data.data;
  }

  // Get daily report summary
  async getDailyReportSummary(startDate: string, endDate: string, project?: string): Promise<ReportSummary> {
    const params: any = { startDate, endDate };
    if (project) params.project = project;
    
    const response = await api.get('/daily-reports/reports/summary', { params });
    return response.data.data;
  }

  // Get toolbox talk summary
  async getToolboxTalkSummary(startDate: string, endDate: string, project?: string): Promise<ReportSummary> {
    const params: any = { startDate, endDate };
    if (project) params.project = project;
    
    const response = await api.get('/toolbox-talks/reports/summary', { params });
    return response.data.data;
  }

  // Get chart data for project performance
  async getProjectPerformanceChart(filters: AnalyticsFilters = {}): Promise<ChartData> {
    const params: any = {};
    if (filters.project) params.project = filters.project;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    
    const response = await api.get('/analytics/charts/project-performance', { params });
    return response.data.data;
  }

  // Get chart data for cost analysis
  async getCostAnalysisChart(filters: AnalyticsFilters = {}): Promise<ChartData> {
    const params: any = {};
    if (filters.project) params.project = filters.project;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    
    const response = await api.get('/analytics/charts/cost-analysis', { params });
    return response.data.data;
  }

  // Get chart data for safety trends
  async getSafetyTrendsChart(filters: AnalyticsFilters = {}): Promise<ChartData> {
    const params: any = {};
    if (filters.project) params.project = filters.project;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    
    const response = await api.get('/analytics/charts/safety-trends', { params });
    return response.data.data;
  }

  // Get chart data for schedule comparison
  async getScheduleComparisonChart(filters: AnalyticsFilters = {}): Promise<ChartData> {
    const params: any = {};
    if (filters.project) params.project = filters.project;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    
    const response = await api.get('/analytics/charts/schedule-comparison', { params });
    return response.data.data;
  }

  // Get AI insights
  async getAIInsights(filters: AnalyticsFilters = {}) {
    const params: any = {};
    if (filters.project) params.project = filters.project;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    
    const response = await api.get('/analytics/ai-insights', { params });
    return response.data.data;
  }

  // Export analytics data
  async exportAnalyticsData(filters: AnalyticsFilters = {}, format = 'pdf') {
    const params = {
      ...filters,
      format,
    };
    
    const response = await api.get('/analytics/export', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  }

  // Get overdue corrective actions
  async getOverdueCorrectiveActions(project?: string) {
    const params: any = {};
    if (project) params.project = project;
    
    const response = await api.get('/safety-reports/corrective-actions/overdue', { params });
    return response.data.data;
  }

  // Get GPS tracking statistics
  async getGPSTrackingStats(filters: AnalyticsFilters = {}) {
    const params: any = {};
    if (filters.project) params.project = filters.project;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    
    const response = await api.get('/gps-tracking/stats/overview', { params });
    return response.data;
  }

  // Get photo documentation statistics
  async getPhotoDocumentationStats(filters: AnalyticsFilters = {}) {
    const params: any = {};
    if (filters.project) params.project = filters.project;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    
    const response = await api.get('/photo-documentation/stats/overview', { params });
    return response.data;
  }

  // Get real-time dashboard updates
  async getRealTimeDashboard(filters: AnalyticsFilters = {}) {
    const params: any = {};
    if (filters.project) params.project = filters.project;
    
    const response = await api.get('/analytics/real-time', { params });
    return response.data.data;
  }

  // Get predictive analytics
  async getPredictiveAnalytics(filters: AnalyticsFilters = {}) {
    const params: any = {};
    if (filters.project) params.project = filters.project;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    
    const response = await api.get('/analytics/predictive', { params });
    return response.data.data;
  }
}

export const analyticsService = new AnalyticsService();
export default analyticsService;